import { Badge } from "../ui/badge";
import { FileChip, fmtDate } from "./OrderShared";

const RESULT_STYLE = { PASS: "text-emerald-600 dark:text-emerald-400", FAIL: "text-destructive", NA: "text-muted-foreground", PENDING: "text-muted-foreground" };

export const ChecklistTable = ({ items, showMeasured, testPrefix }) => (
  <div className="overflow-x-auto rounded-lg border border-border">
    <table className="w-full text-xs" data-testid={`${testPrefix}-checklist`}>
      <thead className="bg-muted/40 text-left">
        <tr>
          <th className="px-3 py-2 font-semibold">Item</th>
          <th className="px-3 py-2 font-semibold">Requirement</th>
          {showMeasured && <th className="px-3 py-2 font-semibold">Measured</th>}
          <th className="px-3 py-2 font-semibold">Result</th>
          {showMeasured && <th className="px-3 py-2 font-semibold">Remarks</th>}
        </tr>
      </thead>
      <tbody>
        {items.map((c) => (
          <tr key={c.key} className="border-t border-border" data-testid={`${testPrefix}-checklist-row-${c.key}`}>
            <td className="px-3 py-2">{c.label}</td>
            <td className="px-3 py-2 text-muted-foreground">{c.requirement || "—"}</td>
            {showMeasured && <td className="px-3 py-2 font-mono-tech">{c.measured_value ? `${c.measured_value} ${c.unit || ""}` : "—"}</td>}
            <td className={`px-3 py-2 font-bold ${RESULT_STYLE[c.result]}`}>{c.result}</td>
            {showMeasured && <td className="px-3 py-2 text-muted-foreground">{c.remarks || "—"}</td>}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/* Read-only QC summary. viewer: "customer" (customer-safe payload) | "partner" (failed items + rework) | "admin" (full) */
export const QualitySummary = ({ order, viewer, onDownload, downloading }) => {
  const q = order.quality;
  if (!q?.inspections?.length) return null;
  const prefix = `${viewer}-order-quality`;
  return (
    <div className="mt-4 space-y-3" data-testid={`${prefix}-${order.id}`}>
      <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground font-mono-tech">Quality inspection · cycle {q.current_cycle}</p>
      {q.inspections.map((i) => (
        <div key={i.id} className="p-4 rounded-lg border border-border space-y-3" data-testid={`${prefix}-inspection-${i.id}`}>
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <Badge variant="secondary">Cycle {i.cycle}</Badge>
            <Badge variant="outline">{i.status}</Badge>
            {i.result && <span className={`font-bold ${RESULT_STYLE[i.result]}`} data-testid={`${prefix}-result-${i.id}`}>{i.result}</span>}
            <span className="text-xs text-muted-foreground">{viewer === "admin" ? `${i.inspector?.email}` : "Vectron QA"}{i.completed_at ? ` · ${fmtDate(i.completed_at)}` : i.started_at ? ` · started ${fmtDate(i.started_at)}` : ""}</span>
          </div>
          {i.remarks && <p className="text-sm text-muted-foreground" data-testid={`${prefix}-remarks-${i.id}`}>{i.remarks}</p>}
          {viewer === "admin" && i.customer_remarks && <p className="text-xs text-muted-foreground">Customer-visible remarks: {i.customer_remarks}</p>}
          {viewer === "partner" && i.failed_items?.length > 0 && <ChecklistTable items={i.failed_items} showMeasured testPrefix={`${prefix}-failed-${i.id}`} />}
          {viewer !== "partner" && i.checklist?.length > 0 && <ChecklistTable items={i.checklist} showMeasured={viewer === "admin"} testPrefix={`${prefix}-${i.id}`} />}
          {viewer !== "customer" && i.rework_instructions && (
            <p className="text-sm p-3 rounded-lg bg-orange-500/5 border border-orange-500/20" data-testid={`${prefix}-rework-${i.id}`}><b>Rework instructions:</b> {i.rework_instructions}</p>
          )}
          {i.evidence?.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {i.evidence.map((f) => <FileChip key={f.id} file={f} onDownload={onDownload} downloading={downloading} testId={`${prefix}-evidence-${f.id}`} />)}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export const DeliverySummary = ({ order, viewer, onDownload, downloading }) => {
  const d = order.delivery;
  if (!d) return null;
  const prefix = `${viewer}-order-delivery`;
  return (
    <div className="mt-4 p-4 rounded-lg border border-border space-y-2 text-sm" data-testid={`${prefix}-${order.id}`}>
      <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground font-mono-tech">Delivery</p>
      {d.ship_to && (
        <p data-testid={`${prefix}-ship-to-${order.id}`}><b>Ship to:</b> {d.ship_to.name}, {d.ship_to.address}, {d.ship_to.city}, {d.ship_to.state} {d.ship_to.pincode}{d.ship_to.phone ? ` · ${d.ship_to.phone}` : ""}{d.ship_to.instructions ? ` · ${d.ship_to.instructions}` : ""}</p>
      )}
      {d.dispatch && (
        <p data-testid={`${prefix}-dispatch-${order.id}`}>
          <b>Dispatched</b> {fmtDate(d.dispatch.dispatched_at)} via <b>{d.dispatch.transporter}</b>{d.dispatch.mode ? ` (${d.dispatch.mode})` : ""}
          {d.dispatch.tracking_ref ? <> · Tracking <span className="font-mono-tech text-primary">{d.dispatch.tracking_ref}</span></> : ""} · {d.dispatch.packages} pkg{d.dispatch.weight ? ` · ${d.dispatch.weight}` : ""}
          {viewer === "admin" ? ` · by ${d.dispatch.dispatched_by}` : ""}
        </p>
      )}
      {d.dispatch?.evidence?.length > 0 && <div className="flex flex-wrap gap-2">{d.dispatch.evidence.map((f) => <FileChip key={f.id} file={f} onDownload={onDownload} downloading={downloading} testId={`${prefix}-dispatch-file-${f.id}`} />)}</div>}
      {d.transit_updates?.length > 0 && (
        <ul className="text-xs text-muted-foreground list-disc pl-5 space-y-0.5" data-testid={`${prefix}-transit-${order.id}`}>
          {d.transit_updates.map((t) => <li key={t.id}>{fmtDate(t.at)} · {t.note}{t.location ? ` (${t.location})` : ""}</li>)}
        </ul>
      )}
      {d.delivered_at && <p data-testid={`${prefix}-delivered-${order.id}`}><b>Delivered</b> {fmtDate(d.delivered_at)}{d.delivery_note ? ` · ${d.delivery_note}` : ""}</p>}
      {d.delivery_evidence?.length > 0 && <div className="flex flex-wrap gap-2">{d.delivery_evidence.map((f) => <FileChip key={f.id} file={f} onDownload={onDownload} downloading={downloading} testId={`${prefix}-pod-${f.id}`} />)}</div>}
      {d.customer_confirmation && <p className="text-emerald-600 dark:text-emerald-400" data-testid={`${prefix}-confirmed-${order.id}`}><b>Delivery confirmed by customer</b> {fmtDate(d.customer_confirmation.confirmed_at)}{d.customer_confirmation.note ? ` · ${d.customer_confirmation.note}` : ""}</p>}
    </div>
  );
};
